import React, { memo, useCallback, useMemo } from 'react';
import { EntryButtons } from './DynamicForm.introduction.buttons';
import {
  Message,
  VoiceWarningBox,
} from './DynamicForm.styles.index';

interface MissingRequiredField {
  name: string;
  label: string;
}

interface DynamicFormProbingProps {
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  missingRequiredFields: MissingRequiredField[];
  retryCount: number;
  maxRetries: number;
  showCancelButton?: boolean;
  showSpeechOption?: boolean;
  showManualButton?: boolean;
  isVoiceActive?: boolean;
  onCancel?: () => void;
}

export const DynamicFormProbing: React.FC<DynamicFormProbingProps> = memo(({
  onManualEntry,
  onSpeechEntry,
  missingRequiredFields,
  retryCount,
  maxRetries,
  showCancelButton = true,
  showSpeechOption = true,
  showManualButton = true,
  isVoiceActive = false,
  onCancel,
}) => {
  const handleManualEntry = useCallback(() => {
    try {
      if (onManualEntry && typeof onManualEntry === 'function') {
        onManualEntry();
      }
    } catch {
      // no-op in UI
    }
  }, [onManualEntry]);

  const handleSpeechEntry = useCallback(() => {
    try {
      if (onSpeechEntry && typeof onSpeechEntry === 'function') {
        onSpeechEntry();
      }
    } catch {
      // no-op in UI
    }
  }, [onSpeechEntry]);

  const handleCancel = useCallback(() => {
    try {
      if (onCancel && typeof onCancel === 'function') {
        onCancel();
      }
    } catch {
      // no-op in UI
    }
  }, [onCancel]);

  // Memoized computed values for better performance
  const probingMessage = useMemo(() => {
    if (!missingRequiredFields || missingRequiredFields.length === 0) {
      return '';
    }

    const fieldBullets = missingRequiredFields.map(f => `• ${f.label}`).join('\n');
    
    return `We're missing the following required fields:\n\n${fieldBullets}\n\nPlease provide this information.`;
  }, [missingRequiredFields]);

  const shouldShowButtons = !isVoiceActive;

  return (
    <>
      <Message role="status" aria-live="polite">
        {probingMessage}
      </Message>

      {isVoiceActive && (
        <VoiceWarningBox>
          Listening... Please speak clearly.
        </VoiceWarningBox>
      )}

      {shouldShowButtons && (
        <EntryButtons
          onManualEntry={handleManualEntry}
          onSpeechEntry={handleSpeechEntry}
          onCancel={handleCancel}
          showCancelButton={showCancelButton}
          showSpeechOption={showSpeechOption}
          showManualButton={showManualButton}
        />
      )}
    </>
  );
});

DynamicFormProbing.displayName = 'DynamicFormProbing';
